import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Input, Textarea } from '@artosera/ui';
import { reglagesRdvModificationSchema } from '@artosera/shared';
import { priseRdvApi, useActionRdv, type ReglagesRdv } from '../../api/priseRendezVous.js';
import { useShellContext } from '../shell/context.js';
import { ErreurRdv } from './ErreurRdv.js';
import styles from './RendezVous.module.css';

export function ReglagesGeneraux({ reglages }: { reglages: ReglagesRdv }) {
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation('regie');
  const mutation = useActionRdv(galerieSlug, (corps: ReglagesRdv, appel) =>
    priseRdvApi.regler(galerieSlug, corps, appel),
  );
  return (
    <section className={styles.bloc}>
      <h2>{t('priseRendezVous.generaux')}</h2>
      <ErreurRdv erreur={mutation.error} />
      <FormulaireGeneraux
        key={reglages.version}
        reglages={reglages}
        pending={mutation.isPending}
        onSauver={(corps) => mutation.mutate(corps)}
      />
      {mutation.isSuccess ? <p role="status">{t('priseRendezVous.enregistre')}</p> : null}
    </section>
  );
}
function FormulaireGeneraux({
  reglages,
  pending,
  onSauver,
}: {
  reglages: ReglagesRdv;
  pending: boolean;
  onSauver: (corps: ReglagesRdv) => void;
}) {
  const { t } = useTranslation(['regie', 'commun']);
  const [valeurs, setValeurs] = useState(reglages);
  const [invalide, setInvalide] = useState(false);
  const listeId = useId();
  return (
    <form
      className={styles.formulaire}
      onSubmit={(e) => {
        e.preventDefault();
        const resultat = reglagesRdvModificationSchema.safeParse(valeurs);
        setInvalide(!resultat.success);
        if (resultat.success) onSauver(resultat.data);
      }}
    >
      <fieldset className={styles.bloc} disabled={pending}>
        <legend>{t('regie:priseRendezVous.reglages')}</legend>
        <div className={styles.grille}>
          <label className={styles.champ}>
            {t('regie:priseRendezVous.fuseau')}
            <Input
              required
              maxLength={100}
              list={listeId}
              value={valeurs.fuseau}
              onChange={(e) => setValeurs({ ...valeurs, fuseau: e.target.value })}
            />
            <datalist id={listeId}>
              {[
                'Europe/Paris',
                'Europe/London',
                'Europe/Zurich',
                'America/New_York',
                'America/Los_Angeles',
                'Asia/Tokyo',
                'Asia/Hong_Kong',
                'Australia/Sydney',
                'UTC',
              ].map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
          </label>
          <label className={styles.champ}>
            {t('regie:priseRendezVous.simultanesMax')}
            <Input
              type="number"
              min={1}
              max={100}
              required
              value={valeurs.simultanesMax}
              onChange={(e) => setValeurs({ ...valeurs, simultanesMax: e.target.valueAsNumber })}
            />
          </label>
          <label className={styles.champ}>
            {t('regie:priseRendezVous.annulationVisiteurHeures')}
            <Input
              type="number"
              min={0}
              max={720}
              step="any"
              required
              value={valeurs.annulationVisiteurHeures}
              onChange={(e) =>
                setValeurs({ ...valeurs, annulationVisiteurHeures: e.target.valueAsNumber })
              }
            />
          </label>
        </div>
        {(['fr', 'en'] as const).map((langue) => (
          <label key={langue} className={styles.champ}>
            {t(`regie:priseRendezVous.messageConfirmation.${langue}`)}
            <Textarea
              maxLength={4000}
              value={valeurs.messageConfirmation[langue]}
              onChange={(e) =>
                setValeurs({
                  ...valeurs,
                  messageConfirmation: { ...valeurs.messageConfirmation, [langue]: e.target.value },
                })
              }
            />
          </label>
        ))}
        <Checkbox
          label={t('regie:priseRendezVous.surLeSite')}
          checked={valeurs.surLeSite}
          onCheckedChange={(v) => setValeurs({ ...valeurs, surLeSite: v === true })}
        />
        <Checkbox
          label={t('regie:priseRendezVous.notifierMembres')}
          checked={valeurs.notifierMembres}
          onCheckedChange={(v) => setValeurs({ ...valeurs, notifierMembres: v === true })}
        />
      </fieldset>
      {invalide ? <p role="alert">{t('regie:priseRendezVous.reglagesInvalides')}</p> : null}
      <Button type="submit" loading={pending}>
        {t('commun:actions.enregistrer')}
      </Button>
    </form>
  );
}
